import React, { useState, useEffect, useRef } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import './SafeCracker.css';
import '../../ui/ArcadeShared.css';
import { FaCheck } from 'react-icons/fa';

// Определяем тип устройства (вычисляется один раз)
const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const SafeCracker = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  // ПОЛУЧАЕМ ДАННЫЕ ИЗ СТОРА (начальное состояние от сервера)
  const initialSession = store.ref.info.game.arcadePublic?.session || {};

  const showTemporaryHint = store.ref.menu.methods.showTemporaryHint;

  const [level, setLevel] = useState(initialSession.level || 1);
  const [score, setScore] = useState(initialSession.score || 0);
  // ВАЖНО: берем codeLength из сессии, а не '3'
  const [codeLength, setCodeLength] = useState(initialSession.codeLength || 3);
  const [inputVal, setInputVal] = useState('');
  const [history, setHistory] = useState(initialSession.history || []);
  const [gameState, setGameState] = useState('playing');

  // Данные финального экрана
  const [finalData, setFinalData] = useState(null);

  const inputRef = useRef(null);

  // Форматирование времени (00:45)
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Определяем цвет таймера (красный, если мало времени)
  const timerColor = timeLeft < 10 ? '#ff3333' : '#e0c097';

  useEffect(() => {
    if (!socket) return;
    const handleEvent = (data) => {
      if (data.type === 'feedback') {
        setHistory(prev => [{
          guess: data.guess,
          bulls: data.bulls,
          cows: data.cows
        }, ...prev]);
        setInputVal('');

        if (data.bulls === 0 && data.cows === 0) {
           window.croco?.haptic?.('error');
        } else {
           window.croco?.haptic?.('light');
        }
      }
      else if (data.type === 'game_over') {
        // Завершение игры по таймеру или другому условию
        window.croco?.haptic?.('success');
        setFinalData(data);
      }
      else if (data.type === 'win') {
        window.croco?.haptic?.('success');
        setScore(data.newScore);
        setGameState('win_round');

        setTimeout(() => {
          setHistory([]);
          setInputVal('');
          setGameState('playing');
          if (data.nextLevelConfig) {
             setLevel(prev => prev + 1);
             setCodeLength(data.nextLevelConfig.length);
          }
          setTimeout(() => {
            if (!isTouchDevice) inputRef.current?.focus();
          }, 100);
        }, 1500);
      }
    };
    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket]);

  // Держим фокус (только на десктопе)
  useEffect(() => {
    const focusInterval = setInterval(() => {
       if (gameState === 'playing' && !isTouchDevice && document.activeElement !== inputRef.current) {
          inputRef.current?.focus();
       }
    }, 1000);
    return () => clearInterval(focusInterval);
  }, [gameState]);

  const handleSend = () => {
    const cleanInput = inputVal.replace(/\D/g, '');
    if (cleanInput.length !== codeLength) {
      showTemporaryHint(t('arcade.safecracker_error_len', { length: codeLength }), { force: true });
      window.croco?.haptic?.('warning');
      return;
    }
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'guess', value: cleanInput });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    if (/^\d*$/.test(val) && val.length <= codeLength) {
      setInputVal(val);
    }
  };

  // Компонент для рендера лампочек
  const RenderBulbs = ({ count, colorClass }) => {
    return Array.from({ length: count }).map((_, i) => (
      <span key={i} className={`bulb ${colorClass}`} />
    ));
  };

  return (
    <div className="safecracker-container" onClick={() => { if (!isTouchDevice) inputRef.current?.focus(); }}>

      {/* 1. Header with Localization & Better Spacing */}
      <div className="safecracker-header">
        <div className="header-left">
          <div className="safecracker-stat">
            {t('arcade.safecracker_level', 'УРОВЕНЬ')} <span className="sc-highlight">{level}</span>
          </div>
          <div className="safecracker-stat">
            {t('arcade.safecracker_code', 'КОД')} <span className="sc-highlight">{codeLength}</span>
          </div>
          <div className="safecracker-stat">
            {t('arcade.safecracker_score', 'СЧЕТ')} <span className="sc-highlight">{score}</span>
          </div>
        </div>
        <div className="header-right">
          <div className="safecracker-stat timer-stat">
            {t('arcade.safecracker_time', 'ВРЕМЯ')} <span style={{ color: timerColor, marginLeft: '5px', fontSize: '15px' }}>{formatTime(timeLeft)}</span>
          </div>
        </div>
      </div>

      {gameState === 'win_round' && (
        <div className="safecracker-overlay">
          <div className="win-title">ОТКРЫТО!</div>
          <div className="win-score">Доступ получен</div>
        </div>
      )}

      {/* History (unchanged logic) */}
      <div className="safecracker-history add-scroll">
         {history.length === 0 && (
            <div style={{textAlign: 'center', opacity: 0.5, marginTop: 'auto', fontStyle: 'italic', fontSize: '14px'}}>
               {t('arcade.safecracker_start_log', 'Ожидание...')}
            </div>
         )}
         {history.map((item, idx) => (
           <div key={idx} className="history-row">
              <span className="history-guess">{item.guess}</span>
              <div className="history-result">
                 <RenderBulbs count={item.bulls} colorClass="bulb-green" />
                 <RenderBulbs count={item.cows} colorClass="bulb-yellow" />
                 {item.bulls === 0 && item.cows === 0 && <span style={{fontSize:'12px', color:'#999'}}>—</span>}
              </div>
           </div>
         ))}
      </div>

      {/* 2. Controls: Left (Input+Btn) & Right (Keypad) */}
      <div className="safecracker-controls">

        {/* Left Column: Input + Big Submit Button */}
        <div className="safecracker-input-column">
           <input
              ref={inputRef}
              className="safecracker-input"
              type="tel"
              value={inputVal}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={"•".repeat(codeLength)}
              inputMode="numeric"
              readOnly={isTouchDevice}
           />

           {/* Big Gold Button */}
           <button className="safecracker-big-btn" onClick={handleSend}>
             {t('arcade.safecracker_enter', 'ВВОД')}
           </button>
        </div>

        {/* Right Column: Keypad (unchanged logic) */}
        <div className="safecracker-keypad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, '<', 0, 'ok'].map((key) => (
            <button
              key={key}
              className={`keypad-btn ${key === 'ok' ? 'keypad-submit' : ''} ${key === '<' ? 'keypad-back' : ''}`}
              onClick={(e) => {
                e.stopPropagation(); // Stop focus loss
                if (!isTouchDevice) inputRef.current?.focus(); // Keep focus on input

                if (key === 'ok') handleSend();
                else if (key === '<') {
                   // Functional update for safety
                   setInputVal(prev => prev.slice(0, -1));
                   window.croco?.haptic?.('selection');
                }
                else if (typeof key === 'number') {
                   if (inputVal.length < codeLength) {
                      setInputVal(prev => prev + key);
                      window.croco?.haptic?.('selection');
                   }
                }
              }}
            >
              {key === 'ok' ? <FaCheck /> : key}
            </button>
          ))}
        </div>
      </div>

      <div className="safecracker-rivets-bottom" />

      {/* ЭКРАН РЕЗУЛЬТАТОВ */}
      {finalData && (
        <div className="safecracker-overlay">
          <div className="final-report">
            <div className="report-header">
              <div className="report-title">ОПЕРАЦИЯ ЗАВЕРШЕНА</div>
            </div>

            <div className="final-stats">
              <div className="final-score">
                {finalData.my.score > 0 ? '+' : ''}{finalData.my.score}
              </div>

              <div className="stat-item">
                <span className="stat-label">Взломано сейфов:</span>
                <span className="stat-value">{Math.max(0, level - 1)}</span>
              </div>

              <div className="stat-item">
                <span className="stat-label">Звание:</span>
                <span className="stat-value rank-title">
                  {finalData.my.score >= 200 ? 'Медвежатник-легенда' :
                   finalData.my.score >= 50 ? 'Опытный взломщик' : 'Мелкий воришка'}
                </span>
              </div>
            </div>

            <UnifiedLeaderboard
                results={finalData.leaderboard}
                myId={store.ref.telegram.telegramUserId}
                t={t}
            />

            <button className="safecracker-close-btn" onClick={onClose}>
              {t('arcade.close_dossier', 'Закрыть дело')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SafeCracker;